import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, ArrowUpRight } from "lucide-react";
import SEO, { breadcrumbSchema } from "../components/SEO";
import Breadcrumb from "../components/Breadcrumb";
import FaqAccordion from "../components/FaqAccordion";
import { COVERAGE_AREAS } from "../data/locationContent";
import { FAQS, faqSchema } from "../data/faqs";
import { SITE } from "../data/seoContent";

// Coverage Areas / Locations page — the local-SEO anchor.
// Targets long-tail "[service] [city]" queries (asbestos survey Oxford, fire risk
// assessment Reading, etc.) plus emits LocalBusiness + FAQPage schema for AEO.
export default function Coverage() {
  const crumbs = [{ name: "Home", path: "/" }, { name: "Coverage", path: "/coverage" }];

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    name: SITE.legalName,
    alternateName: ["OEC", "Oxford Environmental Consultants"],
    url: SITE.domain,
    telephone: "+443456868868",
    email: "enquiries@oxford-ec.co.uk",
    image: `${SITE.domain}/og-default.jpg`,
    priceRange: "££",
    address: {
      "@type": "PostalAddress",
      streetAddress: "Basildon",
      addressLocality: "Basildon",
      addressRegion: "Essex",
      addressCountry: "GB",
    },
    areaServed: COVERAGE_AREAS.map((c) => ({ "@type": "City", name: c.name })),
    serviceType: [
      "Asbestos Survey",
      "Asbestos Management Survey",
      "Asbestos Refurbishment Survey",
      "Asbestos Demolition Survey",
      "Fire Risk Assessment",
      "Fire Door Inspection",
      "Flat Entrance Door Inspection",
      "Compartmentation Survey",
      "Fire Stopping Survey",
      "Passive Fire Protection Survey",
      "Emergency Lighting Testing",
      "Fire Alarm Servicing",
      "Legionella Risk Assessment",
      "Mould Survey",
      "Property Compliance Services",
    ],
  };

  return (
    <div data-testid="page-coverage">
      <SEO
        title="UK Coverage — Asbestos, Fire Safety & Property Compliance | OEC"
        description="Oxford Environmental Consultants delivers asbestos surveys, fire risk assessments, fire door inspections, legionella risk assessments and full property compliance services across London, Oxford, Essex, Kent, Suffolk, Cambridgeshire, Hertfordshire and Berkshire — from our Basildon, Essex base."
        path="/coverage"
        schema={[breadcrumbSchema(crumbs), localBusinessSchema, faqSchema(FAQS.coverage)]}
      />

      <section className="bg-[#0A0F1C] text-white pt-16 pb-24 lg:pt-24 lg:pb-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="mb-8"><Breadcrumb items={crumbs} theme="dark" /></div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/50 mb-6">Coverage</p>
          <h1 className="font-display text-5xl lg:text-7xl font-bold tracking-tight max-w-4xl leading-[1.05]">
            UK property compliance — <span className="gs-gradient-text">delivered locally.</span>
          </h1>
          <p className="mt-8 text-lg text-white/70 max-w-3xl leading-relaxed">
            Asbestos surveys, fire risk assessments, fire door inspections, compartmentation
            surveys, legionella risk assessments, mould surveys and the full property compliance
            suite — delivered from our Basildon, Essex headquarters across <strong>London, Oxford,
            Cambridge, Reading, Watford, St Albans, Stevenage, Bury St Edmunds, Ipswich, Leiston,
            Dartford, Medway and Maidstone</strong>.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <a href="tel:03456868868" className="bg-white text-[#0A0F1C] font-bold px-6 py-3 flex items-center gap-2 hover:opacity-90" data-testid="coverage-call">
              <Phone className="w-4 h-4" /> 03456 868 868
            </a>
            <a href="mailto:enquiries@oxford-ec.co.uk" className="border border-white/30 text-white font-semibold px-6 py-3 flex items-center gap-2 hover:bg-white hover:text-[#0A0F1C]" data-testid="coverage-email">
              <Mail className="w-4 h-4" /> enquiries@oxford-ec.co.uk
            </a>
          </div>
        </div>
      </section>

      <section className="bg-white py-20 lg:py-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <p className="text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-5">15 core coverage cities</p>
          <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 max-w-3xl">Where we work most.</h2>
          <p className="mt-6 text-lg text-slate-600 max-w-2xl">National UK delivery, anchored by deep regional presence in the South East and East of England. If your portfolio sits outside these cities, we still serve you — these are simply the markets we run the most projects in.</p>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200" data-testid="coverage-grid">
            {COVERAGE_AREAS.map((c) => (
              <div key={c.slug} className="bg-white p-7 flex flex-col" data-testid={`coverage-${c.slug}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2">{c.region}</p>
                    <h3 className="font-display text-2xl font-semibold text-slate-900 flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-[#1FA59A]" /> {c.name}
                    </h3>
                  </div>
                </div>
                <p className="text-sm text-slate-600 mt-4 leading-relaxed">{c.focus}.</p>
                <Link to="/contact" state={{ city: c.name }} className="mt-6 text-sm font-semibold text-[#1FA59A] inline-flex items-center gap-2 hover:gap-3 transition-all">
                  Request a quote for {c.name} <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#F8FAFC] py-16 border-t border-slate-200">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
          <p className="text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-4">National & beyond</p>
          <h2 className="font-display text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">Working outside the South East?</h2>
          <p className="mt-4 text-slate-600 max-w-2xl">For large portfolios, multi-region property managers and short-notice mobilisations, we travel anywhere in the UK. Recent projects have run from Aberdeen down to Cornwall — get in touch for a no-obligation scoping call.</p>
          <div className="mt-8">
            <Link to="/contact" className="gs-gradient text-white font-bold px-7 py-3.5 inline-block hover:opacity-90" data-testid="coverage-contact">Talk to a consultant</Link>
          </div>
        </div>
      </section>

      <FaqAccordion faqs={FAQS.coverage} title="Coverage area FAQs" />
    </div>
  );
}
